// Zahlenformate der Oberflaeche. Entsprechen `backend/farmining/core/formatting.py`.

import { TAU } from './math';

const UNITS = ['', 'K', 'M', 'B', 'T', 'Qa', 'Qi', 'Sx', 'Sp', 'Oc', 'No', 'Dc'];

/** 1234567 -> "1.2M" — im Stil des Originals. */
export function fmt(n: number): string {
  if (!isFinite(n)) return '∞';
  if (n < 0) return '-' + fmt(-n);
  if (n < 1000) return n < 10 && n % 1 !== 0 ? n.toFixed(1) : String(Math.floor(n));
  let unit = 0;
  while (n >= 1000 && unit < UNITS.length - 1) { n /= 1000; unit++; }
  return (n < 100 ? n.toFixed(1) : String(Math.floor(n))) + UNITS[unit];
}

/** Prozentwert fuer Tooltips: 1.05 -> "105%" */
export const pct = (mul: number): string => Math.round(mul * 100) + '%';

/** Sekunden -> "45s", "1:05", "2:34:07" */
export function timeStr(s: number): string {
  s = Math.max(0, Math.ceil(s));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  const p = (v: number): string => String(v).padStart(2, '0');
  if (h > 0) return `${h}:${p(m)}:${p(sec)}`;
  return m > 0 ? `${m}:${p(sec)}` : `${sec}s`;
}

/** Weltentfernung -> "11.800 m" */
export const distStr = (d: number): string => `${Math.round(d).toLocaleString('de-DE')} m`;

/** Wandelt Zeichen um, die im HTML eine Bedeutung haetten. */
export function escapeHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/**
 * Die acht Himmelsrichtungen, im Uhrzeigersinn ab Norden.
 *
 * Sie stehen hier und nicht bei den Fundstellen, weil sie **Text formen** und
 * nicht rechnen — und weil sonst `sim/gear.ts` die Kisten importieren muesste,
 * waehrend `sim/caches.ts` umgekehrt den Geraeteriegel braucht: ein Zyklus
 * (Gate FG-05). Die Basisschicht ist der gemeinsame Nenner.
 *
 * „Norden" ist hier **oben im Bild**, also negatives y — die Welt hat keinen
 * Kompass, und der Spieler liest die Richtung von seinem Bildschirm ab. Eine
 * mathematisch korrekte Konvention, die auf dem Schirm um 180° daneben liegt,
 * wäre ein Hinweis, der zuverlässig in die falsche Richtung schickt.
 */
const RICHTUNGEN = [
  'Norden', 'Nordosten', 'Osten', 'Südosten',
  'Süden', 'Südwesten', 'Westen', 'Nordwesten',
] as const;

/** Die Himmelsrichtung eines Punktes von der Kapsel aus gesehen. */
export function directionOf(x: number, y: number): string {
  // atan2(x, -y) legt die Null nach oben und dreht im Uhrzeigersinn — genau die
  // Reihenfolge der Tabelle. Ein Sektor ist ein Achtel, also wird auf
  // Achtel gerundet und nicht abgeschnitten: „fast Nordosten" soll Nordosten
  // heißen und nicht Norden.
  const winkel = Math.atan2(x, -y);
  const sektor = Math.round((winkel / TAU) * 8);
  return RICHTUNGEN[((sektor % 8) + 8) % 8];
}

const HINT_STEP = 500;
/**
 * Wie grob eine Peilnotiz ihre Entfernung nennt.
 *
 * Eine Notiz nennt keine Meter. Die Zahl wird **nie angezeigt** — angezeigt wird
 * `distStr` des gerundeten Werts —, also greift FG-31 nicht: was der Spieler
 * liest, kommt aus `balancing()` (Saat, Streuung, Band, Ringabstand). Die 500
 * ist eine Aussage über die Lesbarkeit einer Notiz, kein Balancing-Knopf; sie
 * ändert nichts an der Welt.
 */

/**
 * Eine Peilnotiz auf einen Ort — **gerundete Richtung und Entfernung**.
 *
 * Sie stand dreimal Zeile für Zeile gleich da (Fundstellen, Fundstücke, und mit
 * dem Wächter wäre die dritte dazugekommen). Hier ist sie einmal, aus demselben
 * Grund wie `directionOf` daneben: sie **formt Text** und rechnet nicht.
 *
 * Gemessen wird vom **Ursprung**, nicht vom Schiff. Eine Notiz überlebt zwei
 * Sitzungen — „18.500 m nach Westen, von hier aus" ist morgen falsch. Und vom
 * Schiff gerechnet wäre sie eine laufende Zahl: entweder fiele sie aus dem
 * Änderungsschlüssel der Liste (dann steht die Seite still und lügt) oder hinein
 * (dann wird die Liste im `requestAnimationFrame`-Takt ersetzt, und der Knopf
 * wird dem Spieler unter dem Finger weggezogen).
 */
export function hintOf(x: number, y: number): string {
  const grob = Math.round(Math.hypot(x, y) / HINT_STEP) * HINT_STEP;
  return `${distStr(grob)} nach ${directionOf(x, y)}`;
}

/**
 * Die Position des Schiffs als lesbare Koordinate.
 *
 * **Mit Himmelsrichtung statt Vorzeichen**, und das ist der ganze Punkt. Die
 * Welt hat kein Vorzeichen, das der Spieler je gesehen hätte — sie hat acht
 * Richtungen (`directionOf`), und in ihnen ist „Norden oben im Bild", also
 * negatives y. Ein rohes „y = −20.400" läse sich für jeden, der eine Landkarte
 * kennt, als **Süden** und schickte ihn in die falsche Richtung. „N 20.400"
 * kann das nicht.
 *
 * Ungerundet und ohne Kurzform: eine Koordinate ist zum **Aufschreiben** da.
 * `fmt` machte daraus „20.4K", und wer danach zurückfliegt, landet bis zu
 * fünfzig Meter daneben — mehr als der halbe Fundradius.
 */
export function coordStr(x: number, y: number): string {
  return `${achse(x, 'O', 'W')} · ${achse(-y, 'N', 'S')}`;
}

/** Eine Achse: Betrag mit Tausenderpunkten, davor ihre Richtung. */
function achse(wert: number, plus: string, minus: string): string {
  const gerundet = Math.round(wert);
  // Die Null bekommt eine feste Richtung statt zu springen: genau auf der Achse
  // wechselte sie sonst bei jedem Meter zwischen „O 0" und „W 0" hin und her.
  const zeichen = gerundet < 0 ? minus : plus;
  return `${zeichen} ${Math.abs(gerundet).toLocaleString('de-DE')}`;
}
